import type { SetStateAction } from 'react';
import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QueryParamsObjectConfig } from '../query-params';
import { generateQueryParamsString, parseQueryParamsString } from '../query-params';
import { useMemoDeepEqual } from './use-memo-deep-equal';

export type SetValue<Value> = (value: SetStateAction<Partial<Value>>, action?: 'replace' | 'push') => void;
type Result<T> = [T, SetValue<T>];

export function useQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
): Result<Partial<TValue>>;

export function useQueryParams<
    TValue extends Record<string, unknown>,
    TDefaultValues extends Partial<TValue>,
>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues: TDefaultValues,
): Result<Partial<TValue> & TDefaultValues>;

export function useQueryParams<TValue extends Record<string, unknown>>(
    config: QueryParamsObjectConfig<TValue>,
    defaultValues?: TValue,
): Result<Partial<TValue>> {
    const [params, setParams] = useSearchParams();

    const paramsRef = useRef(params);
    paramsRef.current = params;

    const value = useMemoDeepEqual(
        () => parseQueryParamsString(params, config, defaultValues as TValue),
        [params, config],
    );

    const valueRef = useRef(value);
    valueRef.current = value;

    const setValue = useCallback<SetValue<TValue>>(
        (nextValueOrFn, action = 'replace') => {
            const nextValue: Partial<TValue> =
                (typeof nextValueOrFn === 'function' ? nextValueOrFn(valueRef.current) : nextValueOrFn) ?? {};

            const query = generateQueryParamsString(config, nextValue, paramsRef.current);

            setParams(query, { replace: action === 'replace' });
        },
        [config],
    );

    return [value, setValue];
}
